/**
 * Portal Explorer Fragment - ES6 Module Version
 * Handles file explorer: select-all, download, delete, folder create form, file upload
 */
import { Events } from '../../../../js/utils/events.js';

let initialized = false;

const CHROME_UPLOAD_LIMIT_BYTES = 10 * 1024 * 1024 * 1024;
const DEFAULT_UPLOAD_LIMIT_BYTES = 4 * 1024 * 1024 * 1024;

function isChromeBrowser() {
    const ua = navigator.userAgent || '';
    return /Chrome/i.test(ua) && !/Edg/i.test(ua) && !/OPR/i.test(ua);
}

function getMaxUploadSizeBytes() {
    return isChromeBrowser() ? CHROME_UPLOAD_LIMIT_BYTES : DEFAULT_UPLOAD_LIMIT_BYTES;
}

function getUploadLimitLabel() {
    return isChromeBrowser() ? '10 GB' : '4 GB';
}

// ============================================================================
// Portal Explorer Handlers
// ============================================================================

const PortalExplorerHandlers = {
    /**
     * Handle "select all" checkbox change
     */
    onSelectAll(e, element) {
        const checkboxes = document.querySelectorAll('.file-checkbox');
        checkboxes.forEach(cb => {
            cb.checked = element.checked;
        });
    },

    /**
     * Handle "select all" checkbox change for search results
     */
    onSearchSelectAll(e, element) {
        const checkboxes = document.querySelectorAll('.file-checkbox');
        checkboxes.forEach(cb => {
            cb.checked = element.checked;
        });
    },

    /**
     * Download selected files from search results
     * Uses direct link navigation to avoid browser memory limits with large files
     */
    downloadSearchSelected(e, element) {
        const portalId = element.dataset.portalFileDownload;

        // Get all selected file checkboxes
        const checkboxes = document.querySelectorAll('.file-checkbox:checked');

        if (checkboxes.length === 0) {
            alert('Please select at least one file to download');
            return;
        }

        // Build query string with selected file OIDs
        const params = new URLSearchParams();
        checkboxes.forEach(checkbox => {
            params.append('file_oids', checkbox.value);
        });

        const url = `/app/portal/file/download/${portalId}?${params.toString()}`;

        // Use direct link navigation for downloads
        // This allows the browser to stream directly to disk instead of buffering in memory
        // Required for large file downloads (>1GB) that would otherwise exceed blob limits
        const a = document.createElement('a');
        a.href = url;
        a.download = '';  // Browser will use Content-Disposition filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    },

    /**
     * Delete selected files from search results with confirmation
     */
    deleteSearchSelected(e, element) {
        const portalId = element.dataset.portalFileDelete;

        // Get all selected file checkboxes
        const checkboxes = document.querySelectorAll('.file-checkbox:checked');

        if (checkboxes.length === 0) {
            alert('Please select at least one file to delete');
            return;
        }

        // Confirm deletion
        if (!confirm('Are you sure you want to delete all selected files?')) {
            return;
        }

        // Get delete button and update state
        const originalText = element.textContent;
        element.textContent = 'Deleting...';
        element.disabled = true;

        // Use HTMX to trigger the delete
        const form = document.getElementById('search-results-form');
        htmx.ajax('POST', `/app/portal/explorer/delete/${portalId}`, {
            source: form,
            swap: 'none'
        }).finally(() => {
            // Always reset button state (page may refresh on success via HX-Refresh)
            element.textContent = originalText;
            element.disabled = false;
        });
    },

    /**
     * Download selected files
     * Uses direct link navigation to avoid browser memory limits with large files
     */
    downloadSelected(e, element) {
        const portalId = element.dataset.portalExplorerDownload;

        // Get all selected file and folder checkboxes
        const fileCheckboxes = document.querySelectorAll('input[name="file_oids"]:checked');
        const folderCheckboxes = document.querySelectorAll('input[name="folder_oids"]:checked');

        if (fileCheckboxes.length === 0 && folderCheckboxes.length === 0) {
            alert('Please select at least one file or folder to download');
            return;
        }

        // Build query string with selected file and folder OIDs
        const params = new URLSearchParams();
        fileCheckboxes.forEach(checkbox => {
            params.append('file_oids', checkbox.value);
        });
        folderCheckboxes.forEach(checkbox => {
            params.append('folder_oids', checkbox.value);
        });

        const url = `/app/portal/file/download/${portalId}?${params.toString()}`;

        // Use direct link navigation for downloads
        // This allows the browser to stream directly to disk instead of buffering in memory
        // Required for large file downloads (>1GB) that would otherwise exceed blob limits
        const a = document.createElement('a');
        a.href = url;
        a.download = '';  // Browser will use Content-Disposition filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    },

    /**
     * Delete selected files with confirmation
     */
    deleteSelected(e, element) {
        const portalId = element.dataset.portalExplorerDelete;

        // Get all selected checkboxes (files and folders)
        const fileCheckboxes = document.querySelectorAll('input[name="file_oids"]:checked');
        const folderCheckboxes = document.querySelectorAll('input[name="folder_oids"]:checked');

        if (fileCheckboxes.length === 0 && folderCheckboxes.length === 0) {
            alert('Please select at least one item to delete');
            return;
        }

        // Confirm deletion
        if (!confirm('Are you sure you want to delete the selected items?')) {
            return;
        }

        // Get delete button and update state
        const originalText = element.textContent;
        element.textContent = 'Deleting...';
        element.disabled = true;

        // Use HTMX to trigger the delete
        const form = document.getElementById('file-explorer-form');
        htmx.ajax('POST', `/app/portal/explorer/delete/${portalId}`, {
            source: form,
            swap: 'none'
        }).then(() => {
            // Success - page will refresh via HX-Refresh header
        }).catch((error) => {
            // Error handling
            console.error('Delete failed:', error);
            element.textContent = originalText;
            element.disabled = false;
            alert('Failed to delete items');
        });
    },

    /**
     * Export to Excel/CSV
     */
    exportFiles(e, element) {
        const portalId = element.dataset.portalExplorerExport;
        const folderOid = element.dataset.portalExplorerExportFolder || '';
        const url = `/app/portal/explorer/export/${portalId}?folder_oid=${folderOid}`;
        window.location.href = url;
    },

    /**
     * Handle folder form before request - disable buttons and show processing
     */
    onFolderFormBeforeRequest(e, element) {
        const saveButton = document.getElementById('save-button');
        const cancelButton = document.getElementById('cancel-button');
        const processingMessage = document.getElementById('processing-message');

        if (saveButton) {
            saveButton.disabled = true;
            saveButton.style.opacity = '0.5';
        }
        if (cancelButton) {
            cancelButton.disabled = true;
            cancelButton.style.opacity = '0.5';
        }
        if (processingMessage) {
            processingMessage.style.display = 'block';
        }
    },

    /**
     * Handle folder form after request - re-enable buttons
     */
    onFolderFormAfterRequest(e, element) {
        const saveButton = document.getElementById('save-button');
        const cancelButton = document.getElementById('cancel-button');
        const processingMessage = document.getElementById('processing-message');

        if (saveButton) {
            saveButton.disabled = false;
            saveButton.style.opacity = '1';
        }
        if (cancelButton) {
            cancelButton.disabled = false;
            cancelButton.style.opacity = '1';
        }
        if (processingMessage) {
            processingMessage.style.display = 'none';
        }
    }
};

// ============================================================================
// Portal File Upload Component
// ============================================================================

const PortalFileUpload = {
    files: [],
    portalOid: null,
    folderOid: null,
    // Upload retry state
    lastError: null,
    uploadState: 'idle', // idle | uploading | failed | completed
    // Track original totals for progress calculation during retry
    originalTotalBytes: 0,
    // Track bytes of successfully completed files (for accurate progress on retry)
    completedBytes: 0,

    init() {
        const uploadElement = document.getElementById('portal-file-upload');
        if (!uploadElement) {
            console.log('PortalFileUpload: portal-file-upload element not found');
            return;
        }

        // Clear files array when initializing
        this.files = [];
        this.lastError = null;
        this.uploadState = 'idle';
        this.originalTotalBytes = 0;
        this.completedBytes = 0;

        this.portalOid = uploadElement.dataset.portalOid;
        // Use empty string for root folder, not null
        this.folderOid = uploadElement.dataset.folderOid || '';

        // Validate portal OID is present
        if (!this.portalOid) {
            console.error('PortalFileUpload: Portal OID is missing from data-portal-oid attribute!');
            console.error('PortalFileUpload: Upload element:', uploadElement);
            console.error('PortalFileUpload: All datasets:', uploadElement.dataset);
        }

        console.log('PortalFileUpload: Initialized with portal OID:', this.portalOid, 'folder OID:', this.folderOid);

        this.setupDropZone();
        this.displayFiles();
        this.updateButtonState();
    },

    setupDropZone() {
        const dropZone = document.getElementById('drop-zone');
        if (!dropZone) {
            console.log('PortalFileUpload: drop-zone element not found');
            return;
        }
        console.log('PortalFileUpload: Setting up drop zone');

        // Prevent default drag behaviors
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, this.preventDefaults, false);
            document.body.addEventListener(eventName, this.preventDefaults, false);
        });

        // Highlight drop zone when item is dragged over it
        ['dragenter', 'dragover'].forEach(eventName => {
            dropZone.addEventListener(eventName, () => {
                dropZone.classList.add('border-teal-600', 'bg-status-info-bg');
            }, false);
        });

        ['dragleave', 'drop'].forEach(eventName => {
            dropZone.addEventListener(eventName, () => {
                dropZone.classList.remove('border-teal-600', 'bg-status-info-bg');
            }, false);
        });

        // Handle dropped files
        const self = this;
        dropZone.addEventListener('drop', async (e) => {
            // MUST prevent default synchronously before async operations
            e.preventDefault();
            e.stopPropagation();

            const dt = e.dataTransfer;

            // Use DataTransferItemList API to handle folders properly
            if (dt.items) {
                const files = await self.getAllFileEntries(dt.items);
                self.handleFiles(files);
            } else {
                // Fallback for older browsers
                self.handleFiles(dt.files);
            }
        }, false);
    },

    // Recursively get all file entries from dropped items (handles folders)
    async getAllFileEntries(dataTransferItemList) {
        const files = [];

        // Convert dataTransferItemList to array and get entries SYNCHRONOUSLY
        // This is critical because DataTransferItem objects are cleared after
        // the event handler's synchronous execution completes
        const entries = [];
        for (let i = 0; i < dataTransferItemList.length; i++) {
            const item = dataTransferItemList[i];
            if (item.kind === 'file') {
                const entry = item.webkitGetAsEntry();
                if (entry) {
                    entries.push(entry);
                }
            }
        }

        // Now process entries asynchronously (safe since we captured them above)
        for (const entry of entries) {
            await this.traverseFileTree(entry, '', files);
        }

        return files;
    },

    // Recursively traverse file tree and collect files
    async traverseFileTree(item, path, files) {
        if (item.isFile) {
            // Get the file
            const file = await new Promise((resolve, reject) => {
                item.file(resolve, reject);
            });

            // Add relative path to file object (mimics webkitRelativePath)
            const relativePath = path + file.name;
            Object.defineProperty(file, 'webkitRelativePath', {
                value: relativePath,
                writable: false
            });

            files.push(file);
        } else if (item.isDirectory) {
            // Get directory reader
            const dirReader = item.createReader();

            // Read all entries in directory
            const entries = await new Promise((resolve, reject) => {
                dirReader.readEntries(resolve, reject);
            });

            // Recursively process each entry
            for (const entry of entries) {
                await this.traverseFileTree(entry, path + item.name + '/', files);
            }
        }
    },

    preventDefaults(e) {
        e.preventDefault();
        e.stopPropagation();
    },

    onSelectFiles() {
        document.getElementById('file-input')?.click();
    },

    onSelectFolder() {
        document.getElementById('folder-input')?.click();
    },

    onFilesSelected(event) {
        const files = event.target.files;
        this.handleFiles(files);
    },

    handleFiles(files) {
        console.log('PortalFileUpload: Handling files:', files.length);
        // Convert FileList to Array
        const filesArray = Array.from(files);

        // Filter out duplicates (matching path and size)
        const uniqueNewFiles = filesArray.filter(newFile => {
            const newPath = newFile.webkitRelativePath || newFile.name;
            const newSize = newFile.size;

            // Check if this file already exists in our files array
            const isDuplicate = this.files.some(existingFile => {
                const existingPath = existingFile.webkitRelativePath || existingFile.name;
                const existingSize = existingFile.size;
                return existingPath === newPath && existingSize === newSize;
            });

            return !isDuplicate; // Only keep non-duplicates
        });

        // Add only unique files to our files array
        const potentialFiles = [...this.files, ...uniqueNewFiles];

        // Check total size before adding files (limit varies by browser)
        const totalSize = potentialFiles.reduce((sum, file) => sum + file.size, 0);
        const maxSize = getMaxUploadSizeBytes();
        const tooLargeFile = potentialFiles.find(file => file.size > maxSize);

        if (tooLargeFile) {
            alert(`File size exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            return;
        }

        if (totalSize > maxSize) {
            alert(`Size of the upload exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            return;
        }

        this.files = potentialFiles;
        console.log('PortalFileUpload: Total files now:', this.files.length);
        this.displayFiles();
        this.updateButtonState();
    },

    displayFiles() {
        const filesList = document.getElementById('files-list');
        if (!filesList) return;

        // Clear current display
        filesList.innerHTML = '';

        // Display each file in simple format
        this.files.forEach((file, index) => {
            const fileItem = document.createElement('div');
            fileItem.className = 'flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50 py-1 px-2 rounded-sm';

            // Use webkitRelativePath if available (folder selection), otherwise use file.name
            // Add leading slash for folder paths
            let displayPath = file.webkitRelativePath || file.name;
            if (file.webkitRelativePath) {
                displayPath = '/' + displayPath;
            }

            fileItem.innerHTML = `
                <span class="truncate flex-1 min-w-0">${this.escapeHtml(displayPath)}</span>
                <span class="ml-4 text-gray-500 shrink-0 whitespace-nowrap">${this.formatFileSize(file.size)}</span>
                <button type="button"
                        class="ml-3 text-gray-400 hover:text-red-600 shrink-0 transition-colors"
                        data-file-index="${index}"
                        title="Remove file">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            `;

            // Add click handler to the remove button
            const removeBtn = fileItem.querySelector('button[data-file-index]');
            if (removeBtn) {
                removeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.removeFile(index);
                });
            }

            filesList.appendChild(fileItem);
        });
    },

    formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    },

    removeFile(index) {
        this.files.splice(index, 1);
        this.displayFiles();
        this.updateButtonState();
    },

    updateButtonState() {
        const saveBtn = document.getElementById('save-btn');
        if (saveBtn) {
            saveBtn.disabled = this.files.length === 0;
        }
    },

    onSave() {
        console.log('[SAVE] onSave called, portalOid:', this.portalOid, 'folderOid:', this.folderOid);

        // Check if there are files to upload
        const totalSize = this.files.reduce((sum, file) => sum + file.size, 0);

        if (this.files.length === 0) {
            alert('Please select files to upload!');
            return;
        }

        if (!this.portalOid) {
            console.error('[SAVE] Portal OID is missing! Current state:', {
                portalOid: this.portalOid,
                folderOid: this.folderOid,
                files: this.files.length
            });
            alert('Portal OID is missing');
            return;
        }

        // Check total file size before upload (limit varies by browser)
        const maxSize = getMaxUploadSizeBytes();
        const tooLargeFile = this.files.find(file => file.size > maxSize);

        if (tooLargeFile) {
            alert(`File size exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            return;
        }

        if (totalSize > maxSize) {
            alert(`Size of the upload exceeds ${getUploadLimitLabel()}!\n\nWe are unable to support uploads that are larger than ${getUploadLimitLabel()} at this time.`);
            return;
        }

        // Start upload
        this.uploadFiles();
    },

    async uploadFiles(isRetry = false) {
        // Initialize upload state
        this.uploadCancelled = false;
        this.currentXHR = null;

        // Disable save button during upload
        const saveBtn = document.getElementById('save-btn');
        if (saveBtn) saveBtn.disabled = true;

        // Show the modal and reset the totals on the first upload, not on retry.
        if (!isRetry) {
            // Store original total for progress calculation during retry
            this.originalTotalBytes = this.files.reduce((sum, file) => sum + file.size, 0);
            this.completedBytes = 0;

            // Show progress modal
            this.showProgressModal();
        }

        try {
            // Use while loop and remove uploaded files from array to prevent re-upload on retry
            while (this.files.length > 0) {
                // Check if upload was cancelled
                if (this.uploadCancelled) {
                    console.log('[UPLOAD] Upload cancelled by user');
                    break;
                }

                // Always process the first file in the array
                const file = this.files[0];
                const isLastFile = (this.files.length === 1);

                await this.uploadFile(file, isLastFile);

                // Track completed bytes for accurate retry progress
                this.completedBytes += file.size;

                // Remove successfully uploaded file from array
                this.files.shift();
                // Update UI to reflect remaining files
                this.displayFiles();
            }

            // Close modal
            this.hideProgressModal();

            // Check if upload was cancelled
            if (this.uploadCancelled) {
                console.log('[UPLOAD] Upload cancelled by user');
                return;
            }

            // Redirect back to explorer
            const redirectUrl = this.folderOid
                ? `/app/portal/explorer/${this.portalOid}?folder_oid=${this.folderOid}`
                : `/app/portal/explorer/${this.portalOid}`;

            console.log('[UPLOAD] Redirecting to:', redirectUrl);
            window.location.href = redirectUrl;
        } catch (error) {
            if (!this.uploadCancelled) {
                // Store error state for retry
                this.uploadState = 'failed';
                this.lastError = error.message;
                console.error('[UPLOAD] Upload failed:', error.message);
                // Show retry UI instead of generic alert
                this.showRetryUI();
            } else {
                this.hideProgressModal();
            }
        } finally {
            // Re-enable save button
            if (saveBtn) saveBtn.disabled = false;
        }
    },

    setProgressBarUI(percent) {
        // Directly update the progress bar UI (used for retry to show correct starting point)
        const progressBar = document.getElementById('modal-progress-bar');
        const progressText = document.getElementById('modal-progress-text');
        if (progressBar) {
            progressBar.style.width = percent + '%';
        }
        if (progressText) {
            progressText.textContent = percent + '%';
        }
    },

    showProgressModal() {
        // Show progress modal (defined in template)
        const modal = document.getElementById('upload-progress-modal');
        if (modal) {
            modal.classList.remove('hidden');
        }
    },

    hideProgressModal() {
        // Hide progress modal
        const modal = document.getElementById('upload-progress-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    },

    showRetryUI() {
        // Change progress bar to red to indicate error
        const progressBar = document.getElementById('modal-progress-bar');
        if (progressBar) {
            progressBar.classList.remove('bg-teal-600');
            progressBar.classList.add('bg-status-danger-text');
        }

        // Show error label
        const errorLabel = document.getElementById('upload-error-label');
        if (errorLabel) {
            errorLabel.classList.remove('hidden');
        }

        // Hide cancel button, show retry buttons
        const cancelSection = document.getElementById('cancel-button-section');
        const retrySection = document.getElementById('retry-button-section');
        if (cancelSection) {
            cancelSection.classList.add('hidden');
        }
        if (retrySection) {
            retrySection.classList.remove('hidden');
        }
    },

    resetProgressModalUI() {
        // Reset progress bar color to blue
        const progressBar = document.getElementById('modal-progress-bar');
        if (progressBar) {
            progressBar.classList.remove('bg-status-danger-text');
            progressBar.classList.add('bg-teal-600');
        }

        // Hide error label
        const errorLabel = document.getElementById('upload-error-label');
        if (errorLabel) {
            errorLabel.classList.add('hidden');
        }

        // Show cancel button, hide retry buttons
        const cancelSection = document.getElementById('cancel-button-section');
        const retrySection = document.getElementById('retry-button-section');
        if (cancelSection) {
            cancelSection.classList.remove('hidden');
        }
        if (retrySection) {
            retrySection.classList.add('hidden');
        }
    },

    async retryUpload() {
        console.log('[UPLOAD] Retrying upload...');
        this.uploadState = 'uploading';
        this.lastError = null;

        // Reset the modal UI back to progress view
        this.resetProgressModalUI();

        // Calculate and set the correct progress percentage based on completed bytes
        const completedPercent = this.originalTotalBytes > 0
            ? Math.floor((this.completedBytes / this.originalTotalBytes) * 100)
            : 0;
        this.setProgressBarUI(completedPercent);
        console.log('[RETRY] Starting from', completedPercent + '%', 'completedBytes:', this.completedBytes, 'totalBytes:', this.originalTotalBytes);

        // Resume upload - this.files still contains the failed file + remaining files
        // Pass isRetry=true to keep the totals from the original attempt
        await this.uploadFiles(true);
    },

    async cancelRetry() {
        console.log('[UPLOAD] Cancel retry requested');
        this.uploadState = 'idle';
        this.lastError = null;
        // Reset the modal UI and hide it
        this.resetProgressModalUI();
        this.hideProgressModal();
    },

    onCancelUpload() {
        console.log('[UPLOAD] Cancel requested');

        // Set cancel flag
        this.uploadCancelled = true;

        // Abort current file upload if in progress
        this.abortCurrentUpload();

        // Hide modal
        this.hideProgressModal();
    },

    async uploadFile(file, isLastFile) {
        // Use webkitRelativePath if available (folder upload), otherwise use file.name
        const fileName = file.webkitRelativePath || file.name;
        console.log('[UPLOAD] Starting upload:', fileName, file.size, 'bytes');

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', '/app/portal/explorer/upload-stream', true);

            // Set headers
            xhr.setRequestHeader('X-Portal-OID', this.portalOid);
            if (this.folderOid) {
                xhr.setRequestHeader('X-Folder-OID', this.folderOid);
            }
            xhr.setRequestHeader('Portal-File-Name', encodeURIComponent(fileName));
            xhr.setRequestHeader('Portal-File-Size', file.size.toString());
            xhr.setRequestHeader('Portal-Last-File', isLastFile ? 'true' : 'false');

            // A hang shows up as absence of movement: xhr.timeout bounds the whole
            // request, so any value short enough to catch one also kills slow multi-GB uploads.
            let stalled = false;
            let watchdog = null;
            const armWatchdog = (ms) => {
                clearTimeout(watchdog);
                watchdog = setTimeout(() => { stalled = true; xhr.abort(); }, ms);
            };

            // Measured in the browser: a server-side counter needs state shared
            // across workers, and split across two it froze the bar at 0%.
            xhr.upload.onprogress = (ev) => {
                armWatchdog(90000);
                if (!ev.lengthComputable || !this.originalTotalBytes) return;
                this.setProgressBarUI(Math.min(100, Math.floor(
                    ((this.completedBytes + ev.loaded) / this.originalTotalBytes) * 100)));
            };

            // Body fully sent: no more progress events, so bound the wait for the
            // response instead. ev.loaded counts bytes handed to the socket, not
            // bytes written to S3, so say what the full bar is still waiting on.
            xhr.upload.onload = () => {
                armWatchdog(180000);
                // Only the last file: mid-queue the bar still has files to climb, so the label reads as a false finish.
                if (!isLastFile) return;
                const header = document.getElementById('modal-header-text');
                if (header) header.textContent = 'Finishing upload...';
            };

            xhr.onload = () => {
                clearTimeout(watchdog);
                if (xhr.status === 200) {
                    try {
                        const result = JSON.parse(xhr.responseText);
                        if (result.success) {
                            console.log('[UPLOAD] File uploaded successfully:', fileName);
                            resolve(result);
                        } else {
                            reject(new Error(result.error || 'Upload failed'));
                        }
                    } catch (e) {
                        reject(new Error('Invalid server response'));
                    }
                } else {
                    try {
                        const error = JSON.parse(xhr.responseText);

                        // Handle storage quota exceeded - redirect to capacity warning
                        if (xhr.status === 403 && error.error && error.error.includes('Storage quota exceeded')) {
                            const params = new URLSearchParams({ message: error.error });
                            htmx.ajax('GET', '/app/account/utilization/capacity-warning?' + params.toString(), {
                                target: '[data-hx-boundary="section"]',
                                swap: 'innerHTML'
                            });
                            reject(new Error(error.error));
                            return;
                        }

                        reject(new Error(error.error || 'Upload failed'));
                    } catch (e) {
                        reject(new Error('Upload failed: ' + xhr.status));
                    }
                }
            };

            xhr.onerror = () => {
                clearTimeout(watchdog);
                reject(new Error('Network error during upload'));
            };

            xhr.onabort = () => {
                clearTimeout(watchdog);
                console.log('[UPLOAD] Upload aborted for file:', fileName);
                reject(new Error(stalled ? 'The server stopped responding.' : 'Upload cancelled'));
            };

            // Store XHR for abort support
            this.currentXHR = xhr;

            // Send raw file body
            const header = document.getElementById('modal-header-text');
            if (header) header.textContent = 'Uploading Files';
            armWatchdog(90000);
            xhr.send(file);
        });
    },

    // Abort current upload
    abortCurrentUpload() {
        if (this.currentXHR) {
            this.currentXHR.abort();
            this.currentXHR = null;
        }
    }
};

// Make PortalFileUpload available globally for onclick handlers in template
// (until template is updated with data attributes)
window.PortalFileUpload = PortalFileUpload;

/**
 * Initialize portal explorer fragment
 * Called from main.js on DOMContentLoaded
 */
export function initPortalExplorer() {
    if (initialized) return;
    initialized = true;

    // Select-all checkbox (file explorer)
    Events.on('change', 'portal-explorer-select-all', PortalExplorerHandlers.onSelectAll);

    // Download, delete, and export buttons (file explorer)
    Events.on('click', 'portal-explorer-download', PortalExplorerHandlers.downloadSelected);
    Events.on('click', 'portal-explorer-delete', PortalExplorerHandlers.deleteSelected);
    Events.on('click', 'portal-explorer-export', PortalExplorerHandlers.exportFiles);

    // Select-all checkbox (search results)
    Events.on('change', 'portal-file-select-all', PortalExplorerHandlers.onSearchSelectAll);

    // Download and delete buttons (search results)
    Events.on('click', 'portal-file-download', PortalExplorerHandlers.downloadSearchSelected);
    Events.on('click', 'portal-file-delete', PortalExplorerHandlers.deleteSearchSelected);

    // Portal file upload event handlers
    Events.on('change', 'portal-upload-file-input', (e) => {
        PortalFileUpload.onFilesSelected(e);
    });

    Events.on('change', 'portal-upload-folder-input', (e) => {
        PortalFileUpload.onFilesSelected(e);
    });

    Events.on('click', 'portal-upload-select-files', () => {
        PortalFileUpload.onSelectFiles();
    });

    Events.on('click', 'portal-upload-select-folder', () => {
        PortalFileUpload.onSelectFolder();
    });

    Events.on('click', 'portal-upload-save', () => {
        PortalFileUpload.onSave();
    });

    Events.on('click', 'portal-upload-cancel', () => {
        PortalFileUpload.onCancelUpload();
    });

    Events.on('click', 'portal-upload-retry', () => {
        PortalFileUpload.retryUpload();
    });

    Events.on('click', 'portal-upload-cancel-retry', () => {
        PortalFileUpload.cancelRetry();
    });

    // Folder form HTMX events - use htmx:beforeRequest/afterRequest on the form
    document.addEventListener('htmx:beforeRequest', (event) => {
        if (event.target.id === 'add-folder-form') {
            PortalExplorerHandlers.onFolderFormBeforeRequest(event, event.target);
        }
    });

    document.addEventListener('htmx:afterRequest', (event) => {
        if (event.target.id === 'add-folder-form') {
            PortalExplorerHandlers.onFolderFormAfterRequest(event, event.target);
        }
    });

    // Initialize file upload on page load if element exists
    if (document.getElementById('portal-file-upload')) {
        console.log('[GLOBAL] Initializing PortalFileUpload on DOMContentLoaded');
        PortalFileUpload.init();
    }

    // Re-initialize file upload after HTMX swaps
    document.addEventListener('htmx:afterSwap', function() {
        if (document.getElementById('portal-file-upload')) {
            console.log('[GLOBAL] Initializing PortalFileUpload on htmx:afterSwap');
            PortalFileUpload.init();
        }
    });
}
